/**
 * resolver/merge.js - Delta merge utilities for style system
 * Location: /app/frontend/src/admin/page_builder/resolver/
 * 
 * Handles merging of sparse delta objects with proper precedence.
 * Supports null values to reset properties to their default.
 */

import { StyleSource, isResetValue } from './types';

/**
 * Merge multiple delta layers with strict precedence.
 * Later layers override earlier ones. Null values reset to previous layer.
 * 
 * @param {Object[]} layers - Array of [delta, source] pairs in precedence order (lowest first)
 * @returns {Object<string, {value: any, source: number}>} Merged result with source tracking
 * 
 * @example
 * mergeDeltaLayers([
 *   [{ color: 'red', fontSize: '16px' }, StyleSource.COMPONENT_DEFAULT],
 *   [{ color: { $token: 'colors.primary' } }, StyleSource.GLOBAL_TOKEN],
 *   [{ fontSize: '18px' }, StyleSource.PAGE_OVERRIDE],
 *   [{ color: null }, StyleSource.ELEMENT_OVERRIDE] // Resets color to page/global value
 * ])
 */
export function mergeDeltaLayers(layers) {
  const result = {};
  
  for (const [delta, source] of layers) {
    if (!delta || typeof delta !== 'object') {
      continue;
    }
    
    for (const [key, value] of Object.entries(delta)) {
      if (isResetValue(value)) {
        // Null means "reset to previous layer" - remove from result
        // so the next lower layer's value will be used
        // But we need to track that this layer explicitly reset it
        // For now, we just delete it so it falls back
        delete result[key];
      } else {
        result[key] = { value, source };
      }
    }
  }
  
  return result;
}

/**
 * Apply element-level overrides on top of a base style object.
 * This is a simpler merge for a single override layer.
 * 
 * @param {Object} base - Base style object
 * @param {Object} overrides - Override delta (sparse)
 * @returns {Object} Merged style object
 */
export function applyOverrides(base, overrides) {
  if (!overrides || typeof overrides !== 'object') {
    return { ...base };
  }
  
  const result = { ...base };
  
  for (const [key, value] of Object.entries(overrides)) {
    if (isResetValue(value)) {
      // Reset to base - just keep base value (already there)
      // If base doesn't have it, remove it
      if (!(key in base)) {
        delete result[key];
      }
    } else {
      result[key] = value;
    }
  }
  
  return result;
}

/**
 * Extract only the delta (changed values) between two style objects.
 * Used when saving - only persist what's different from defaults.
 * 
 * @param {Object} current - Current style values
 * @param {Object} baseline - Baseline/default values
 * @returns {Object} Delta object with only changed values (null for removals)
 */
export function extractDelta(current, baseline) {
  const delta = {};
  
  // Check for changed or new values
  for (const [key, value] of Object.entries(current || {})) {
    const baseValue = baseline?.[key];
    
    // Deep equality check for objects (like token refs)
    const isEqual = JSON.stringify(value) === JSON.stringify(baseValue);
    
    if (!isEqual) {
      delta[key] = value;
    }
  }
  
  // Check for removed values (in baseline but not in current)
  for (const key of Object.keys(baseline || {})) {
    if (!(key in (current || {}))) {
      delta[key] = null; // Explicit reset
    }
  }
  
  return delta;
}

/**
 * Check if a delta object is empty (no actual overrides)
 * @param {Object} delta - Delta object to check
 * @returns {boolean} True if delta has no meaningful values
 */
export function isDeltaEmpty(delta) {
  if (!delta || typeof delta !== 'object') {
    return true;
  }
  return Object.keys(delta).length === 0;
}

/**
 * Clean a delta object by removing null values (for display purposes)
 * @param {Object} delta - Delta object
 * @returns {Object} Cleaned delta without null entries
 */
export function cleanDelta(delta) {
  if (!delta || typeof delta !== 'object') {
    return {};
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(delta)) {
    if (!isResetValue(value)) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

export default {
  mergeDeltaLayers,
  applyOverrides,
  extractDelta,
  isDeltaEmpty,
  cleanDelta
};
